// The offer package card: the attribute chips, the package versions and the six §6 rows in his order. Row 1 is locked
// from the Finalized compensation review. The Hiring Manager's projection carries rows 1-3, the status and the final
// terms with the Limited tag; rows 4-6 are not drawn at all (the server never sent them).
import type { ReactNode } from "react";
import { Card, Chip, Chips, COMPENSATION_REVIEW_STATUS, KeyValue, OFFER_PACKAGE_STATUS, Pill, Tag, TAG_LABELS, VersionTags, type KeyValueItem } from "@/mc";
import type { Amount, CompOut, CompRange, PackageStatus } from "./CompensationTab";
import { PKG_FIELD_LABELS, PKG_LABELS, PKG_ROW_LABELS } from "./labels";

const NONE = "—";

/** A figure in the package's currency (or the bare number when no currency is known). */
export function money(value: Amount, currency?: string | null): string {
  if (value === null || value === undefined || value === "") return NONE;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return currency
    ? new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(n)
    : new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
}

const range = (r: CompRange | null | undefined): string =>
  r && (r.min !== undefined || r.max !== undefined) ? `${money(r.min, r.currency)} – ${money(r.max, r.currency)}` : NONE;

/** One "field value" pair inside a row's value. */
const pair = (label: string, value: string): ReactNode => (
  <span className="mc-kv-pair" key={label}>{label} <span className="mc-mono">{value}</span></span>
);
const pairs = (items: [string, string][]): ReactNode => <span className="mc-kv-pairs">{items.map(([l, v]) => pair(l, v))}</span>;

export const PACKAGE_STATUS_WORD: Record<PackageStatus, string> = {
  draft: OFFER_PACKAGE_STATUS.draft,
  pending_approvals: OFFER_PACKAGE_STATUS.pendingApprovals,
  ready_for_verbal: OFFER_PACKAGE_STATUS.readyForVerbalOffer,
  rejected: OFFER_PACKAGE_STATUS.rejected,
  revision_required: OFFER_PACKAGE_STATUS.revisionRequired,
};

const positive = (value: Amount): boolean => value !== null && value !== undefined && value !== "" && Number(value) > 0;

export interface PackageCardProps {
  comp: CompOut;
  limited: boolean;
}

export function PackageCard({ comp, limited }: PackageCardProps) {
  const pkg = comp.package ?? null;
  const currency = pkg?.currency ?? comp.recommendation?.range?.currency ?? null;
  const reco = comp.recommendation;
  const row1 = [reco?.text, range(reco?.range)].filter((part) => part && part !== NONE).join(" · ") || NONE;
  const items: KeyValueItem[] = [
    [PKG_ROW_LABELS.row1, row1, COMPENSATION_REVIEW_STATUS.finalized, 3],
    [PKG_ROW_LABELS.row2, pairs([
      [PKG_FIELD_LABELS.proposedBase, money(pkg?.base, currency)],
      [PKG_FIELD_LABELS.bonus, money(pkg?.bonus_target, currency)],
      [PKG_FIELD_LABELS.commission, money(pkg?.commission, currency)],
    ]), undefined, 3],
    [PKG_ROW_LABELS.row3, pairs([
      [PKG_FIELD_LABELS.signOn, money(pkg?.sign_on, currency)],
      [PKG_FIELD_LABELS.relocation, money(pkg?.relocation, currency)],
      [PKG_FIELD_LABELS.lti, money(pkg?.lti, currency)],
      [PKG_LABELS.status, pkg ? PACKAGE_STATUS_WORD[pkg.status] : NONE],
    ]), undefined, 3],
  ];
  if (limited) {
    items.push([PKG_LABELS.status, pkg ? PACKAGE_STATUS_WORD[pkg.status] : NONE]);
    items.push([PKG_LABELS.finalTerms, pkg?.final_terms ?? NONE, undefined, 2]);
  } else {
    const market = comp.market ?? {};
    items.push([PKG_ROW_LABELS.row4, comp.salary_grade ?? NONE, undefined, 3]);
    items.push([PKG_ROW_LABELS.row5, [market.data, market.internal_range, market.positioning].filter(Boolean).join(" · ") || NONE, undefined, 3]);
    const equity = comp.internal_comparison ? Object.values(comp.internal_comparison).filter((v) => typeof v === "string").join(" · ") : "";
    items.push([PKG_ROW_LABELS.row6, [equity, comp.working_notes].filter(Boolean).join(" · ") || NONE, undefined, 3]);
  }
  const versions = [...new Set([...(comp.history ?? []).map((h) => h.version).filter((v): v is number => typeof v === "number"), ...(pkg ? [pkg.version] : [])])]
    .sort((a, b) => a - b)
    .map((n) => ({ n, hs: n === pkg?.version ? "offer.pkg.versions" : undefined }));
  return (
    <Card
      title={PKG_LABELS.offerPackage}
      icon="dollar"
      
      pills={
        <>
          {pkg ? <Pill>{PACKAGE_STATUS_WORD[pkg.status]}</Pill> : <Pill>{OFFER_PACKAGE_STATUS.draft}</Pill>}
          {limited ? <Tag tone="limited">{TAG_LABELS.limited}</Tag> : null}
        </>
      }
    >
      <Chips>
        <Chip on sm mode="view">{PKG_LABELS.standardOffer}</Chip>
        {positive(pkg?.sign_on) ? <Chip on sm mode="view">{PKG_LABELS.signOn}</Chip> : null}
        {positive(pkg?.relocation) ? <Chip on sm mode="view">{PKG_LABELS.relocation}</Chip> : null}
        {positive(pkg?.lti) ? <Chip on sm mode="view">{PKG_LABELS.lti}</Chip> : null}
      </Chips>
      {pkg && versions.length > 0 ? <VersionTags versions={versions} current={pkg.version} label={PKG_LABELS.offerPackage} /> : null}
      <KeyValue items={items} cols={3} />
    </Card>
  );
}
